import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getAuthHeader } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, MessageSquare, Trash2, RefreshCw, Download } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [signups, setSignups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const headers = getAuthHeader();
      const [signupsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/survey-signups`, { headers }),
        axios.get(`${API}/contacts`, { headers })
      ]);
      setSignups(signupsRes.data);
      setContacts(contactsRes.data);
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoadingData(false);
    }
  };

  const deleteSignup = async (email) => {
    try {
      await axios.delete(`${API}/survey-signups/${encodeURIComponent(email)}`, { headers: getAuthHeader() });
      setSignups(signups.filter(s => s.email !== email));
    } catch (err) {
      console.error('Failed to delete signup', err);
    }
  };

  const deleteContact = async (email) => {
    try {
      await axios.delete(`${API}/contacts/${encodeURIComponent(email)}`, { headers: getAuthHeader() });
      setContacts(contacts.filter(c => c.email !== email));
    } catch (err) {
      console.error('Failed to delete contact', err);
    }
  };

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csv = [headers.join(','), ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-[#0A192F] font-bold text-lg">Loading...</div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  return (
    <div data-testid="admin-dashboard" className="min-h-[calc(100vh-80px)] bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-sm text-[#475569] mt-1">Welcome back, {user.name}</p>
          </div>
          <button
            onClick={fetchData}
            data-testid="refresh-data-btn"
            className="neo-btn px-5 py-2.5 bg-white text-[#0A192F] text-sm flex items-center gap-2"
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div className="neo-card p-6 flex items-center gap-5">
            <div className="w-14 h-14 rounded-xl bg-[#166534] text-white flex items-center justify-center">
              <Users size={24} />
            </div>
            <div>
              <p className="text-3xl font-black text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{signups.length}</p>
              <p className="text-sm text-[#475569] font-medium">Survey Sign-ups</p>
            </div>
          </div>
          <div className="neo-card p-6 flex items-center gap-5">
            <div className="w-14 h-14 rounded-xl bg-[#DC2626] text-white flex items-center justify-center">
              <MessageSquare size={24} />
            </div>
            <div>
              <p className="text-3xl font-black text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{contacts.length}</p>
              <p className="text-sm text-[#475569] font-medium">Contact Messages</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="signups" className="w-full">
          <TabsList className="bg-white border-2 border-[#0A192F] p-1 rounded-xl mb-6">
            <TabsTrigger
              value="signups"
              data-testid="tab-signups"
              className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-lg px-6 py-2 font-semibold text-sm"
            >
              Survey Sign-ups
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              data-testid="tab-contacts"
              className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-lg px-6 py-2 font-semibold text-sm"
            >
              Contact Messages
            </TabsTrigger>
          </TabsList>

          <TabsContent value="signups">
            <div className="neo-card overflow-hidden">
              <div className="p-4 flex items-center justify-between border-b-2 border-[#0A192F]">
                <h3 className="text-lg font-bold text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Sign-ups ({signups.length})</h3>
                <button
                  onClick={() => exportCSV(signups, 'skoop-signups.csv')}
                  data-testid="export-signups-btn"
                  className="neo-btn px-4 py-2 bg-[#38BDF8] text-[#0A192F] text-xs flex items-center gap-2"
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>
              {loadingData ? (
                <div className="p-8 text-center text-[#475569]">Loading...</div>
              ) : signups.length === 0 ? (
                <div className="p-8 text-center text-[#475569]">No sign-ups yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold text-[#0A192F]">Name</TableHead>
                      <TableHead className="font-bold text-[#0A192F]">Email</TableHead>
                      <TableHead className="font-bold text-[#0A192F]">Zip Code</TableHead>
                      <TableHead className="font-bold text-[#0A192F]">Date</TableHead>
                      <TableHead className="font-bold text-[#0A192F] w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signups.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{s.first_name}</TableCell>
                        <TableCell>{s.email}</TableCell>
                        <TableCell>{s.zip_code}</TableCell>
                        <TableCell className="text-sm text-[#475569]">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => deleteSignup(s.email)}
                            data-testid={`delete-signup-${i}`}
                            className="p-2 rounded-lg hover:bg-[#DC2626]/10 text-[#DC2626] transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="neo-card overflow-hidden">
              <div className="p-4 flex items-center justify-between border-b-2 border-[#0A192F]">
                <h3 className="text-lg font-bold text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages ({contacts.length})</h3>
                <button
                  onClick={() => exportCSV(contacts, 'skoop-contacts.csv')}
                  data-testid="export-contacts-btn"
                  className="neo-btn px-4 py-2 bg-[#38BDF8] text-[#0A192F] text-xs flex items-center gap-2"
                >
                  <Download size={14} /> Export CSV
                </button>
              </div>
              {loadingData ? (
                <div className="p-8 text-center text-[#475569]">Loading...</div>
              ) : contacts.length === 0 ? (
                <div className="p-8 text-center text-[#475569]">No messages yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-bold text-[#0A192F]">Name</TableHead>
                      <TableHead className="font-bold text-[#0A192F]">Email</TableHead>
                      <TableHead className="font-bold text-[#0A192F]">Subject</TableHead>
                      <TableHead className="font-bold text-[#0A192F]">Message</TableHead>
                      <TableHead className="font-bold text-[#0A192F]">Date</TableHead>
                      <TableHead className="font-bold text-[#0A192F] w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{c.first_name} {c.last_name}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <TableCell>{c.subject}</TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-[#475569]">{c.message}</TableCell>
                        <TableCell className="text-sm text-[#475569]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => deleteContact(c.email)}
                            data-testid={`delete-contact-${i}`}
                            className="p-2 rounded-lg hover:bg-[#DC2626]/10 text-[#DC2626] transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
